import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Users, Mail, Trash2, Loader2, Send, ShieldCheck, Building2, Crown, Copy, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const APP_ORIGIN =
  process.env.REACT_APP_PUBLIC_APP_URL ||
  (typeof window !== 'undefined' ? window.location.origin : '');

const ROLE_LABEL = {
  agency: 'Care Provider',
  discharge_planner: 'Healthcare Facility',
};

export default function OrgTeamPage() {
  const { getAuthHeader, user } = useAuth();
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);

  // Invite form state
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/org/team`, getAuthHeader());
      setTeam(data);
    } catch (e) {
      toast.error('Could not load your team.');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const sendInvite = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    try {
      await axios.post(
        `${API}/org/invitations`,
        { email: email.trim(), first_name: firstName.trim() || null, note: note.trim() || null },
        getAuthHeader(),
      );
      toast.success(`Invitation sent to ${email}`);
      setEmail(''); setFirstName(''); setNote('');
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not send invitation';
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const revoke = async (id) => {
    if (!window.confirm('Revoke this invitation?')) return;
    try {
      await axios.delete(`${API}/org/invitations/${id}`, getAuthHeader());
      toast.success('Invitation revoked');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not revoke');
    }
  };

  const copyLink = (inv) => {
    // The accept URL embeds the token; for security we never store the token
    // client-side after creation — but the user can copy a fresh-from-creation
    // link directly from the toast that fires on `sendInvite`. Here we copy a
    // helper message instead, since the persisted invite no longer has the token.
    const text = `${inv.org_name || 'Your team'} invitation for ${inv.email} — ask your colleague to check their email for the magic link, or resend from CaraLoom.`;
    navigator.clipboard.writeText(text);
    setCopiedId(inv.id);
    toast.success('Reminder copied');
    setTimeout(() => setCopiedId(null), 1500);
  };

  // Sidebar that matches the inviting role.
  const isFacility = user?.role === 'discharge_planner';
  const dashHref = isFacility ? '/planner/dashboard' : '/agency/dashboard';
  const navItems = [
    { icon: isFacility ? Building2 : Users, label: 'Dashboard', href: dashHref },
    { icon: Users, label: 'Team', active: true },
    { icon: ShieldCheck, label: 'Account security', href: '#security' },
  ];

  const memberCount = team?.member_count || 1;
  const pending = team?.pending_invitations || [];
  const members = team?.members || [];

  return (
    <DashboardShell
      navItems={navItems}
      title="Team"
      subtitle={`Invite colleagues to join ${team?.owner?.first_name ? team?.owner?.first_name + "'s" : 'your'} organization on CaraLoom.`}
      testId="org-team-page"
    >
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => <div key={i} className="cn-card h-24 animate-pulse bg-cn-bg/60" />)}
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* LEFT — Invite form */}
          <form onSubmit={sendInvite} className="cn-card p-6 space-y-4 lg:col-span-1" data-testid="org-invite-form">
            <div className="flex items-center gap-2 mb-1">
              <Send className="w-4 h-4 text-cn-primary" />
              <h3 className="font-display font-semibold text-lg text-cn-text dark:text-white">Invite a teammate</h3>
            </div>
            <p className="text-xs text-cn-text-muted">
              They&apos;ll receive a magic-link email. Once they accept, they get
              their own login and see this organization&apos;s dashboard.
            </p>
            <div>
              <Label htmlFor="invite-email" className="text-[10px] uppercase font-bold text-cn-text-muted">Work email *</Label>
              <Input id="invite-email" type="email" value={email}
                     onChange={(e) => setEmail(e.target.value)} required
                     placeholder="colleague@example.com"
                     className="mt-1" data-testid="org-invite-email" />
            </div>
            <div>
              <Label htmlFor="invite-name" className="text-[10px] uppercase font-bold text-cn-text-muted">First name (optional)</Label>
              <Input id="invite-name" value={firstName}
                     onChange={(e) => setFirstName(e.target.value)}
                     placeholder="Alex" className="mt-1" data-testid="org-invite-first-name" />
            </div>
            <div>
              <Label htmlFor="invite-note" className="text-[10px] uppercase font-bold text-cn-text-muted">Personal note (optional)</Label>
              <Textarea id="invite-note" value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Heya — adding you to the CaraLoom team so you can start running referrals…"
                        className="mt-1 min-h-[80px]" data-testid="org-invite-note" />
            </div>
            <Button type="submit" disabled={sending || !email.trim()}
                    className="cn-btn cn-btn-primary !py-2.5 w-full" data-testid="org-invite-submit">
              {sending
                ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Sending…</>
                : <><Send className="w-4 h-4 mr-1" /> Send invitation</>}
            </Button>
          </form>

          {/* RIGHT — Members + Pending */}
          <div className="lg:col-span-2 space-y-6">
            <div className="grid grid-cols-2 gap-3" data-testid="org-team-stats">
              <Stat label="Team size" value={memberCount} icon={Users} />
              <Stat label="Pending invitations" value={pending.length} icon={Mail} />
            </div>

            {/* Members */}
            <div className="cn-card p-6" data-testid="org-members-list">
              <div className="flex items-center gap-2 mb-4">
                <Crown className="w-4 h-4 text-amber-600" />
                <h3 className="font-display font-semibold text-lg text-cn-text dark:text-white">
                  Members ({memberCount})
                </h3>
              </div>
              <ul className="divide-y divide-cn-border">
                {team?.owner && (
                  <MemberRow m={team.owner} role={ROLE_LABEL[team.owner.role] || team.owner.role} />
                )}
                {members.map((m) => (
                  <MemberRow key={m.id} m={m} role={ROLE_LABEL[m.role] || m.role} />
                ))}
              </ul>
              {memberCount === 1 && (
                <p className="text-xs text-cn-text-muted mt-4">
                  Just you so far. Invite a colleague using the form on the left.
                </p>
              )}
            </div>

            {/* Pending */}
            <div className="cn-card p-6" data-testid="org-pending-list">
              <div className="flex items-center gap-2 mb-4">
                <Mail className="w-4 h-4 text-cn-primary" />
                <h3 className="font-display font-semibold text-lg text-cn-text dark:text-white">
                  Pending invitations ({pending.length})
                </h3>
              </div>
              {pending.length === 0 ? (
                <p className="text-sm text-cn-text-muted">No pending invitations.</p>
              ) : (
                <ul className="space-y-2">
                  {pending.map((inv) => (
                    <li key={inv.id}
                        className="flex items-center justify-between gap-4 px-4 py-3 rounded-lg border border-cn-border"
                        data-testid={`pending-${inv.id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-sm text-cn-text dark:text-white truncate">
                          {inv.first_name ? `${inv.first_name} · ` : ''}{inv.email}
                        </p>
                        <p className="text-xs text-cn-text-muted">
                          Invited {new Date(inv.created_at).toLocaleDateString()} · expires {new Date(inv.expires_at).toLocaleDateString()}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => copyLink(inv)}
                        title="Copy reminder text"
                        className="text-xs text-cn-primary hover:underline flex items-center gap-1"
                        data-testid={`pending-${inv.id}-copy`}
                      >
                        {copiedId === inv.id ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                        {copiedId === inv.id ? 'Copied' : 'Remind'}
                      </button>
                      <button
                        type="button"
                        onClick={() => revoke(inv.id)}
                        title="Revoke invitation"
                        className="text-xs text-cn-terracotta-600 hover:underline flex items-center gap-1"
                        data-testid={`pending-${inv.id}-revoke`}
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Revoke
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {APP_ORIGIN && (
                <p className="text-[10px] text-cn-text-muted mt-3">
                  Accept links are sent to the teammate&apos;s email. They&apos;ll land at <code>{APP_ORIGIN}/invite/&lt;token&gt;</code>.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </DashboardShell>
  );
}

const Stat = ({ label, value, icon: Icon }) => (
  <div className="cn-card p-4">
    <div className="flex items-center justify-between">
      <span className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">{label}</span>
      <Icon className="w-4 h-4 text-cn-primary" />
    </div>
    <div className="mt-1 font-display font-bold text-2xl text-cn-text dark:text-white">{value}</div>
  </div>
);

const MemberRow = ({ m, role }) => (
  <li className="flex items-center justify-between py-3" data-testid={`member-${m.id}`}>
    <div className="min-w-0 flex-1">
      <p className="font-semibold text-sm text-cn-text dark:text-white truncate flex items-center gap-2">
        {m.first_name || ''} {m.last_name || ''}
        {m.is_owner && (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
            <Crown className="w-3 h-3" /> Owner
          </span>
        )}
      </p>
      <p className="text-xs text-cn-text-muted truncate">{m.email}</p>
    </div>
    <span className="text-xs text-cn-text-muted bg-cn-bone-100 px-2 py-1 rounded-md whitespace-nowrap">{role}</span>
  </li>
);
